import { create } from 'zustand';

import { getView } from '../engine/prism.js';
import { setOverlay } from '../engine/host.js';
import { docIO, SAVE_DELAY_MS } from '../lib/doc-io.js';
import { eventSocket } from '../lib/ws.js';
import {
  buildPreviewRequest,
  defaultOutput,
  depGraphFromDescribe,
  idFromName,
  instanceName,
  newProcgenDoc,
  normalizeDiagnostics,
  previewSettingsFor,
  PROCGEN_EXT,
  serializeProcgen,
  setAtPath,
  tryParseProcgen,
  type DepGraph,
  type PreviewProbe,
  type PreviewResult,
  type PreviewSettings,
  type ProcgenDiagnostic,
  type ProcgenDoc,
} from '../components/procgen/procgen-doc.js';
import {
  browserBackends,
  invalidateProcgenFile,
  mergeResult,
  mergeRgba,
  procgenWorkerReason,
  runCheck,
  runPreview,
  syncProcgenFile,
  type PreviewSource,
  type ProcgenBackends,
} from '../components/procgen/procgen-preview.js';
import { logEditor } from './console.js';
import { useProjectFiles } from './project-files.js';
import { useWorkspace } from './workspace.js';

/**
 * The Procedural panel's open generator (`*.procgen.json`,
 * engine/docs/systems/generators.md section 10.4).
 *
 * The text is the source of truth (unknown keys survive every edit); the
 * parsed document is derived. Each edit goes on the undo stack, is written to
 * the engine overlay and announced to the Scene view (`fileChanged`) so the
 * live scene regenerates, autosaves after a short debounce, and re-runs the
 * check and the preview. The store subscribes to file-change events itself.
 */

export type ProcgenSaveState = 'saved' | 'pending' | 'saving' | 'error';
export type PreviewStatus = 'idle' | 'queued' | 'running' | 'done' | 'error';

export interface PreviewState {
  status: PreviewStatus;
  result: PreviewResult | null;
  /** Full-size RGBA (progressively filled). */
  image: Uint8ClampedArray | null;
  source: PreviewSource | null;
  error: string | null;
  warnings: string[];
  /** Diagnostics the preview reported (its graph build). */
  diagnostics: ProcgenDiagnostic[];
  /** Milliseconds of the last complete run. */
  ms: number | null;
  /** Increments per finished chunk (canvas redraw key). */
  version: number;
}

export interface ProbeState {
  x: number;
  z: number;
  result: PreviewProbe | null;
  error: string | null;
}

const IDLE_PREVIEW: PreviewState = {
  status: 'idle',
  result: null,
  image: null,
  source: null,
  error: null,
  warnings: [],
  diagnostics: [],
  ms: null,
  version: 0,
};

export interface ProcgenState {
  path: string | null;
  text: string;
  doc: ProcgenDoc | null;
  /** JSON syntax error of the current text (PG001). */
  parseError: string | null;
  loading: boolean;
  error: string | null;
  saveState: ProcgenSaveState;
  past: string[];
  future: string[];
  diagnostics: ProcgenDiagnostic[];
  checking: boolean;
  checkSource: PreviewSource | null;
  settings: PreviewSettings;
  /** Re-run the preview after each edit. */
  autoPreview: boolean;
  preview: PreviewState;
  probe: ProbeState | null;
  /** Dependency layers from the engine (`describe`), when a response carried them. */
  graphInfo: DepGraph | null;
  /** Last `procgenStatus` runtime event (scene hosts). */
  runtimeStatus: Record<string, unknown> | null;
  /** JSON path focused from a diagnostic (the inspector scrolls to it). */
  focus: string | null;

  open: (path: string) => Promise<void>;
  close: () => void;
  /** Replaces the whole text (raw JSON edit). */
  setText: (text: string, options?: { history?: boolean }) => void;
  /** Structured edit of the parsed document (ignored while the text does not parse). */
  edit: (fn: (doc: ProcgenDoc) => ProcgenDoc, options?: { history?: boolean }) => void;
  /** Sets (or with `undefined` removes) the value at a JSON path. */
  setAt: (path: string, value: unknown, options?: { history?: boolean }) => void;
  undo: () => void;
  redo: () => void;
  flush: () => Promise<void>;
  /** A project file changed on disk. */
  fileChanged: (path: string) => void;
  check: () => Promise<void>;
  setSettings: (patch: Partial<PreviewSettings>) => void;
  setAutoPreview: (on: boolean) => void;
  /** Writes the current preview settings into `editor.preview`. */
  pinSettings: () => void;
  runPreview: () => Promise<void>;
  /** Previews an expression ("show where true"). */
  showExpression: (expr: string) => void;
  probeAt: (x: number, z: number) => Promise<void>;
  clearProbe: () => void;
  setFocus: (path: string | null) => void;
  /** A `procgenStatus` engine event. */
  statusFromEngine: (event: Record<string, unknown>) => void;
  /** Creates `<dir>/<id>.procgen.json` from newProcgenDoc() and opens it. */
  createNew: (dir?: string, name?: string) => Promise<string | null>;
}

const HISTORY_LIMIT = 200;
const CHECK_DELAY_MS = 500;
const PREVIEW_DELAY_MS = 350;
/** Progressive chunks per preview run (each `maxSamples` items). */
const MAX_CHUNKS = 256;

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let checkTimer: ReturnType<typeof setTimeout> | null = null;
let previewTimer: ReturnType<typeof setTimeout> | null = null;
let previewSeq = 0;
let probeSeq = 0;
let checkSeq = 0;
/** Text last read from / written to disk, per path. */
const lastDisk = new Map<string, string>();
/** Preview settings per generator for this session. */
const settingsByPath = new Map<string, PreviewSettings>();

const projectId = (): number | null => useWorkspace.getState().projectId ?? null;
const errorText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

let backendsOverride: ((flush: () => Promise<void>) => ProcgenBackends) | null = null;

/** Replace the preview / check backends (tests). Pass null to restore the browser ones. */
export function setProcgenBackends(factory: ((flush: () => Promise<void>) => ProcgenBackends) | null): void {
  backendsOverride = factory;
}

let attached = false;

/** Subscribes the store to the project's file-change events (once). */
export function attachProcgenSync(): void {
  if (attached) return;
  attached = true;
  eventSocket.subscribe((event) => {
    if (event.type !== 'file.changed' || event.projectId !== useWorkspace.getState().projectId) return;
    useProcgen.getState().fileChanged(event.path);
  });
  // The open generator belongs to the open project.
  useWorkspace.subscribe((state, prev) => {
    if (state.projectId === prev.projectId || !useProcgen.getState().path) return;
    // Never write the old project's generator into the new project.
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = null;
    useProcgen.setState({ saveState: 'saved' });
    useProcgen.getState().close();
  });
}

/** Tells the live scene about a generator text (overlay + hot reload). */
function pushLive(path: string, text: string): void {
  setOverlay(path, text);
  syncProcgenFile(path, text);
  void getView('scene')?.fileChanged(path);
}

export const useProcgen = create<ProcgenState>((set, get) => {
  function backends(): ProcgenBackends {
    const flush = () => get().flush();
    return backendsOverride ? backendsOverride(flush) : browserBackends(projectId(), flush);
  }

  function scheduleSave(): void {
    if (saveTimer) clearTimeout(saveTimer);
    set({ saveState: 'pending' });
    saveTimer = setTimeout(() => {
      saveTimer = null;
      void get().flush();
    }, SAVE_DELAY_MS);
  }

  function scheduleCheck(): void {
    if (checkTimer) clearTimeout(checkTimer);
    checkTimer = setTimeout(() => {
      checkTimer = null;
      void get().check();
    }, CHECK_DELAY_MS);
  }

  function schedulePreview(delay = PREVIEW_DELAY_MS): void {
    if (!get().autoPreview || !get().path) return;
    if (previewTimer) clearTimeout(previewTimer);
    set((s) => ({ preview: { ...s.preview, status: s.preview.status === 'running' ? 'running' : 'queued' } }));
    previewTimer = setTimeout(() => {
      previewTimer = null;
      void get().runPreview();
    }, delay);
  }

  /** Applies a new text: history, live scene, save, check, preview. */
  function commit(text: string, options: { history?: boolean; save?: boolean } = {}): void {
    const { history = true, save = true } = options;
    const state = get();
    if (!state.path || text === state.text) return;
    const parsed = tryParseProcgen(text);
    set({
      text,
      doc: parsed.doc ?? state.doc,
      parseError: parsed.error,
      past: history ? [...state.past, state.text].slice(-HISTORY_LIMIT) : state.past,
      future: history ? [] : state.future,
    });
    pushLive(state.path, text);
    if (save) scheduleSave();
    if (parsed.doc) {
      scheduleCheck();
      schedulePreview();
    } else {
      set({
        diagnostics: [{ severity: 'error', code: 'PG001', file: state.path, path: '', span: null, message: parsed.error ?? 'invalid JSON', help: null, fixes: [], related: [] }],
      });
    }
  }

  function loaded(path: string, text: string): void {
    lastDisk.set(path, text);
    const parsed = tryParseProcgen(text);
    const settings = settingsByPath.get(path) ?? previewSettingsFor(parsed.doc);
    set({
      path,
      text,
      doc: parsed.doc,
      parseError: parsed.error,
      loading: false,
      error: null,
      saveState: 'saved',
      past: [],
      future: [],
      diagnostics: [],
      settings,
      preview: IDLE_PREVIEW,
      probe: null,
      graphInfo: null,
      focus: null,
    });
    pushLive(path, text);
    if (parsed.doc) {
      scheduleCheck();
      schedulePreview(50);
    }
  }

  return {
    path: null,
    text: '',
    doc: null,
    parseError: null,
    loading: false,
    error: null,
    saveState: 'saved',
    past: [],
    future: [],
    diagnostics: [],
    checking: false,
    checkSource: null,
    settings: previewSettingsFor(null),
    autoPreview: true,
    preview: IDLE_PREVIEW,
    probe: null,
    graphInfo: null,
    runtimeStatus: null,
    focus: null,

    open: async (path) => {
      attachProcgenSync();
      const pid = projectId();
      if (!pid) return;
      if (get().path === path && get().doc) return;
      await get().flush();
      set({ path, loading: true, error: null, doc: null, text: '', preview: IDLE_PREVIEW, diagnostics: [] });
      try {
        const text = await docIO().read(pid, path);
        if (get().path !== path) return;
        loaded(path, text);
      } catch (error) {
        if (get().path === path) set({ loading: false, error: `${path}: ${errorText(error)}` });
      }
    },

    close: () => {
      void get().flush();
      for (const t of [saveTimer, checkTimer, previewTimer]) if (t) clearTimeout(t);
      saveTimer = checkTimer = previewTimer = null;
      previewSeq++;
      set({ path: null, text: '', doc: null, parseError: null, error: null, saveState: 'saved', past: [], future: [], diagnostics: [], preview: IDLE_PREVIEW, probe: null, graphInfo: null, focus: null });
    },

    setText: (text, options) => commit(text, options),

    edit: (fn, options) => {
      const { doc } = get();
      if (!doc || get().parseError) return;
      // Work on a copy so a mutating `fn` never changes the current state.
      const next = fn(structuredClone(doc));
      commit(serializeProcgen(next), options);
    },

    setAt: (path, value, options) => get().edit((doc) => setAtPath(doc, path, value), options),

    undo: () => {
      const { past, text, future, path } = get();
      const prev = past[past.length - 1];
      if (prev === undefined || !path) return;
      set({ past: past.slice(0, -1), future: [text, ...future] });
      commit(prev, { history: false });
    },

    redo: () => {
      const { past, text, future, path } = get();
      const next = future[0];
      if (next === undefined || !path) return;
      set({ past: [...past, text], future: future.slice(1) });
      commit(next, { history: false });
    },

    flush: async () => {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = null;
      const { path, text, saveState } = get();
      const pid = projectId();
      if (!path || !pid || saveState === 'saved') return;
      if (text === lastDisk.get(path)) {
        set({ saveState: 'saved' });
        return;
      }
      set({ saveState: 'saving' });
      try {
        lastDisk.set(path, text);
        await docIO().write(pid, path, text);
        if (get().path === path && get().saveState === 'saving') set({ saveState: get().text === text ? 'saved' : 'pending' });
      } catch (error) {
        lastDisk.delete(path);
        set({ saveState: 'error' });
        logEditor('error', `Could not save ${path}: ${errorText(error)}`);
      }
    },

    fileChanged: (path) => {
      invalidateProcgenFile(path);
      const { path: open, saveState, doc } = get();
      if (!open) return;
      if (path === open) {
        if (saveState !== 'saved') return;
        const pid = projectId();
        if (!pid) return;
        void docIO()
          .read(pid, path)
          .then((text) => {
            if (get().path !== path || text === lastDisk.get(path) || text === get().text) return;
            lastDisk.set(path, text);
            commit(text, { save: false });
          })
          .catch(() => undefined);
        return;
      }
      // An input (or its script / prefab) changed: the result may differ.
      const deps = doc ? JSON.stringify([doc.inputs ?? {}, doc.script ?? null, doc.assets ?? []]) : '';
      if (path.endsWith(PROCGEN_EXT) || path.endsWith('.rhai') || deps.includes(path)) {
        scheduleCheck();
        schedulePreview(200);
      }
    },

    check: async () => {
      const { path, doc } = get();
      if (!path || !doc) return;
      const seq = ++checkSeq;
      set({ checking: true });
      try {
        const out = await runCheck([path], backends());
        if (seq !== checkSeq || get().path !== path) return;
        set({ diagnostics: out.diagnostics, checkSource: out.source, checking: false });
      } catch (error) {
        if (seq !== checkSeq) return;
        set({ checking: false, checkSource: null });
        logEditor('warn', `Procedural check unavailable: ${errorText(error)}`);
      }
    },

    setSettings: (patch) => {
      const settings = { ...get().settings, ...patch };
      const path = get().path;
      if (path) settingsByPath.set(path, settings);
      set({ settings });
      schedulePreview(150);
    },

    setAutoPreview: (on) => {
      set({ autoPreview: on });
      if (on) schedulePreview(50);
    },

    pinSettings: () => {
      const s = get().settings;
      get().edit((doc) => ({
        ...doc,
        editor: {
          ...(doc.editor ?? {}),
          preview: { show: s.show, rect: s.rect, size: s.size, seed: s.seed, y: s.y, slice: s.slice, shade: s.shade, ramp: s.ramp, weights: s.weights, overlays: s.overlays, overlayWhere: s.overlayWhere },
        },
      }));
    },

    runPreview: async () => {
      const { path, doc } = get();
      if (!path || !doc) return;
      if (previewTimer) clearTimeout(previewTimer);
      previewTimer = null;
      const seq = ++previewSeq;
      const started = Date.now();
      const instance = instanceName(doc, path);
      const out = defaultOutput(doc);
      const defaultShow = out ? `${instance}.${out}` : null;
      set((s) => ({ preview: { ...s.preview, status: 'running', error: null } }));
      let image: Uint8ClampedArray | null = null;
      let merged: PreviewResult | null = null;
      let cursor = 0;
      const warnings: string[] = [];
      try {
        for (let chunk = 0; chunk < MAX_CHUNKS; chunk++) {
          const settings = get().settings;
          const request = buildPreviewRequest({ graph: [path], settings, defaultShow, cursor });
          const outcome = await runPreview(request, backends());
          if (seq !== previewSeq) return;
          for (const w of outcome.warnings) if (!warnings.includes(w)) warnings.push(w);
          const r = outcome.result;
          const size: [number, number] = Array.isArray(r.size) ? r.size : request.size ?? settings.size;
          image = mergeRgba(chunk === 0 ? null : image, size, r);
          merged = chunk === 0 ? r : mergeResult(merged, r);
          const diagnostics = normalizeDiagnostics(r.diagnostics ?? []);
          const graph = depGraphFromDescribe((r as { graph?: unknown }).graph);
          const failed = r.ok === false && !r.rgba;
          const status: PreviewStatus = failed ? 'error' : r.done === false ? 'running' : 'done';
          set((s) => ({
            preview: {
              status,
              result: merged,
              image,
              source: outcome.source,
              error: failed ? (r.error ?? diagnostics.find((d) => d.severity === 'error')?.message ?? 'preview failed') : null,
              warnings: [...warnings],
              diagnostics,
              ms: r.done === false ? s.preview.ms : Date.now() - started,
              version: s.preview.version + 1,
            },
            ...(graph ? { graphInfo: graph } : {}),
          }));
          if (failed || r.done !== false || typeof r.cursor !== 'number' || r.cursor <= cursor) break;
          cursor = r.cursor;
        }
      } catch (error) {
        if (seq !== previewSeq) return;
        const reason = procgenWorkerReason();
        set((s) => ({
          preview: { ...s.preview, status: 'error', error: errorText(error) + (reason ? ` (${reason})` : ''), warnings },
        }));
      }
    },

    showExpression: (expr) => {
      const { doc, path } = get();
      if (!expr.trim() || !doc || !path) return;
      get().setSettings({ show: expr.trim(), scope: instanceName(doc, path) });
    },

    probeAt: async (x, z) => {
      const { path, doc, settings } = get();
      if (!path || !doc) return;
      const seq = ++probeSeq;
      set({ probe: { x, z, result: get().probe?.result ?? null, error: null } });
      const out = defaultOutput(doc);
      const request = buildPreviewRequest({
        graph: [path],
        settings: { ...settings, rect: [x - 0.5, z - 0.5, x + 0.5, z + 0.5], size: [1, 1], overlays: [], overlayWhere: {}, maxSamples: 1 },
        defaultShow: out ? `${instanceName(doc, path)}.${out}` : null,
        probe: [x, settings.y, z],
      });
      try {
        const outcome = await runPreview(request, backends());
        if (seq !== probeSeq) return;
        set({ probe: { x, z, result: outcome.result.probe ?? null, error: outcome.result.ok === false ? (outcome.result.error ?? 'probe failed') : null } });
      } catch (error) {
        if (seq === probeSeq) set({ probe: { x, z, result: null, error: errorText(error) } });
      }
    },

    clearProbe: () => {
      probeSeq++;
      set({ probe: null });
    },

    setFocus: (path) => set({ focus: path }),

    statusFromEngine: (event) => set({ runtimeStatus: event }),

    createNew: async (dir = 'gen', name = 'New Generator') => {
      const pid = projectId();
      if (!pid) return null;
      const entries = useProjectFiles.getState().entries;
      const taken = new Set(entries.map((e) => e.path));
      const base = idFromName(name);
      let path = `${dir.replace(/\/+$/, '')}/${base}${PROCGEN_EXT}`;
      for (let i = 2; taken.has(path); i++) path = `${dir.replace(/\/+$/, '')}/${base}_${i}${PROCGEN_EXT}`;
      try {
        await docIO().write(pid, path, newProcgenDoc(name));
      } catch (error) {
        logEditor('error', `Could not create ${path}: ${errorText(error)}`);
        return null;
      }
      useProjectFiles.getState().refreshSoon();
      await get().open(path);
      return path;
    },
  };
});
